/**
 * TESSILE PDP reviews — fetch from jazpro.ro and render into .ts-reviews-block
 * containers (one on mobile inside the accordion, one on desktop in the
 * dedicated section). Both containers are found via querySelectorAll, but
 * only one is visible per viewport thanks to CSS in tessile.css.
 *
 * The endpoint:
 *   GET https://www.jazpro.ro/tessile_reviews/api/reviews.php
 *     ?handle=<product_handle>&page=N&per_page=10[&sort=newest]
 *
 * Returns:
 *   { reviews: [...], stats: {avg, count}, page, total_pages, total }
 *
 * One fetch per unique handle even if two containers exist (the cache below).
 */
(function () {
  'use strict';

  var API = 'https://www.jazpro.ro/tessile_reviews/api/reviews.php';
  var PER_PAGE = 5;

  // Promise cache so two containers (mobile + desktop) for the same product
  // only hit the network once per page state.
  var cache = {};

  function fetchPage(handle, page) {
    var key = handle + '|' + page;
    if (cache[key]) return cache[key];
    var url = API + '?handle=' + encodeURIComponent(handle)
            + '&page=' + page + '&per_page=' + PER_PAGE;
    cache[key] = fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      });
    return cache[key];
  }

  // Renders the 5-star overlay used everywhere in this UI.
  // Returns string of HTML.
  function starsHTML(rating, size) {
    var pct = Math.max(0, Math.min(5, rating)) * 20;
    var cls = (size === 'sm') ? 'ts-stars ts-stars--sm' : 'ts-stars ts-stars--lg';
    return '<span class="' + cls + '" role="img" aria-label="' + rating + ' din 5"'
         + ' style="--fill: ' + pct + '%">'
         + '<span class="ts-stars__row ts-stars__row--bg" aria-hidden="true">★★★★★</span>'
         + '<span class="ts-stars__row ts-stars__row--fg" aria-hidden="true">★★★★★</span>'
         + '</span>';
  }

  function escapeHTML(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Format a YYYY-MM-DD date into Romanian "12 oct 2024" form.
  var MONTHS = ['ian','feb','mar','apr','mai','iun','iul','aug','sep','oct','noi','dec'];
  function fmtDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return iso || '';
    return parseInt(m[3], 10) + ' ' + MONTHS[parseInt(m[2], 10) - 1] + ' ' + m[1];
  }

  function renderReview(r) {
    var titleRow = '<div class="ts-review__title-row">'
      + (r.title ? '<h4 class="ts-review__title">' + escapeHTML(r.title) + '</h4>' : '')
      + starsHTML(r.rating, 'sm')
      + '</div>';

    var author = '<p class="ts-review__author">'
      + escapeHTML(r.name)
      + '<span class="ts-review__verified">✓ Achiziție verificată</span>'
      + '</p>';

    var body = '<p class="ts-review__body">' + escapeHTML(r.body) + '</p>';

    var reply = '';
    if (r.reply_text) {
      reply = '<div class="ts-review__reply">'
        + '<span class="ts-review__reply-label">Răspuns TESSILE</span>'
        + escapeHTML(r.reply_text)
        + '</div>';
    }

    return '<article class="ts-review">' + titleRow + author + body + reply + '</article>';
  }

  function renderPagination(container, data) {
    var pages = data.total_pages;
    if (pages <= 1) {
      container.innerHTML = '';
      return;
    }
    var page = data.page;
    container.innerHTML =
        '<button type="button" data-page="' + (page - 1) + '"'
          + (page <= 1 ? ' disabled' : '') + '>← Anterior</button>'
      + '<span class="ts-reviews-block__pagination__page-info">Pagina ' + page + ' din ' + pages + '</span>'
      + '<button type="button" data-page="' + (page + 1) + '"'
          + (page >= pages ? ' disabled' : '') + '>Următor →</button>';
  }

  function loadInto(container, page) {
    var handle = container.getAttribute('data-handle');
    if (!handle) return;
    var pagination = container.parentElement.querySelector('.ts-reviews-block__pagination');

    container.innerHTML = '<p class="ts-reviews-block__loading">Se încarcă recenziile…</p>';

    fetchPage(handle, page).then(function (data) {
      if (!data.reviews || data.reviews.length === 0) {
        container.innerHTML = '<p class="ts-reviews-block__loading">Nu există încă recenzii pentru acest produs.</p>';
        if (pagination) pagination.innerHTML = '';
        return;
      }
      container.innerHTML = data.reviews.map(renderReview).join('');
      if (pagination) renderPagination(pagination, data);
      // Mobile: append "(N)" to the accordion summary heading on first load.
      if (page === 1) updateAccordionSummary(container, data.stats && data.stats.count);
    }).catch(function () {
      container.innerHTML = '<p class="ts-reviews-block__loading">Recenziile nu pot fi încărcate momentan. Încearcă din nou mai târziu.</p>';
      if (pagination) pagination.innerHTML = '';
    });
  }

  // Append "(N)" to the mobile accordion summary heading. Horizon renders
  // the heading text as a bare text node inside <summary> alongside the
  // icon <span>s — so we mutate only the first non-empty text node, NOT
  // the summary's textContent (which would wipe the +/- icons).
  //
  // Skipped when count is 0 — no "(0)" suffix in the empty state.
  function updateAccordionSummary(container, count) {
    if (!count || count <= 0) return;
    var accordion = container.closest('.ts-reviews--accordion');
    if (!accordion) return;
    var details = accordion.closest('details');
    if (!details) return;
    var summary = details.querySelector('summary');
    if (!summary) return;
    var nodes = summary.childNodes;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) {
        var t = n.textContent;
        if (t.indexOf('(') !== -1) return;          // already appended
        n.textContent = t.replace(/\s*$/, '') + ' (' + count + ') ';
        return;
      }
    }
  }

  // ---------- Submission form ----------
  var SUBMIT_API = 'https://www.jazpro.ro/tessile_reviews/api/submit.php';

  function bindRatingPicker(form) {
    var stars = form.querySelectorAll('.ts-rating-picker__star');
    var hidden = form.querySelector('input[name="rating"]');
    var selected = 0;

    function paint(n) {
      stars.forEach(function (s, i) {
        s.classList.toggle('is-selected', i < n);
        s.classList.toggle('is-hovered', false);
      });
    }
    function hoverPaint(n) {
      stars.forEach(function (s, i) {
        s.classList.toggle('is-hovered', i < n);
      });
    }
    stars.forEach(function (star) {
      var v = parseInt(star.getAttribute('data-value'), 10);
      star.addEventListener('mouseenter', function () { hoverPaint(v); });
      star.addEventListener('focus',      function () { hoverPaint(v); });
      star.addEventListener('mouseleave', function () { paint(selected); });
      star.addEventListener('blur',       function () { paint(selected); });
      star.addEventListener('click', function () {
        selected = v;
        hidden.value = String(v);
        paint(selected);
        clearFieldError(form, 'rating');
      });
    });
  }

  function clearFieldError(form, name) {
    var err = form.querySelector('.ts-reviews-form__error[data-for="' + name + '"]');
    if (err) { err.hidden = true; err.textContent = ''; }
    var input = form.querySelector('[name="' + name + '"]');
    if (input) input.classList.remove('is-invalid');
  }

  // Map server-side validation codes → Romanian messages
  var ERROR_MESSAGES = {
    rating_invalid:  'Te rugăm să selectezi un rating de la 1 la 5 stele.',
    body_length:     'Recenzia trebuie să aibă între 10 și 2000 de caractere.',
    name_length:     'Numele trebuie să aibă între 2 și 80 de caractere.',
    title_length:    'Titlul trebuie să aibă între 3 și 150 de caractere.',
    email_invalid:   'Adresa de email nu este validă.',
    handle_invalid:  'Eroare internă. Reîncarcă pagina și încearcă din nou.'
  };
  var FIELD_FOR_CODE = {
    rating_invalid: 'rating', body_length: 'body', name_length: 'name',
    title_length:   'title',  email_invalid: 'email', handle_invalid: 'rating'
  };

  function showFormMessage(form, type, text) {
    var msg = form.querySelector('.ts-reviews-form__message');
    if (!msg) return;
    msg.className = 'ts-reviews-form__message is-' + type;
    msg.textContent = text;
    msg.hidden = false;
  }

  function showSuccess(form) {
    form.innerHTML =
      '<h3 class="ts-reviews-form__title">Mulțumim!</h3>'
      + '<p style="text-align:center;line-height:1.55;margin:0;color:#4a4a4a;">'
      + 'Recenzia ta este în curs de aprobare. Va fi publicată în scurt timp.'
      + '</p>';
  }

  function bindSubmitForm(form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // Clear previous errors
      ['rating','title','body','name','email'].forEach(function (n) {
        clearFieldError(form, n);
      });
      var msg = form.querySelector('.ts-reviews-form__message');
      if (msg) { msg.hidden = true; msg.textContent = ''; }

      var submitBtn = form.querySelector('.ts-reviews-form__submit');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Se trimite…';

      var payload = {
        handle: form.getAttribute('data-handle'),
        rating: parseInt(form.querySelector('input[name="rating"]').value || '0', 10),
        title:  form.querySelector('[name="title"]').value.trim(),
        body:   form.querySelector('[name="body"]').value.trim(),
        name:   form.querySelector('[name="name"]').value.trim(),
        email:  form.querySelector('[name="email"]').value.trim()
      };

      fetch(SUBMIT_API, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
      .then(function (res) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Trimite recenzia';

        if (res.body && res.body.ok) {
          showSuccess(form);
          return;
        }
        var err = res.body || {};
        if (err.error === 'validation' && Array.isArray(err.fields)) {
          err.fields.forEach(function (code) {
            var field = FIELD_FOR_CODE[code];
            if (!field) return;
            var errEl = form.querySelector('.ts-reviews-form__error[data-for="' + field + '"]');
            if (errEl) {
              errEl.textContent = ERROR_MESSAGES[code] || 'Verifică acest câmp.';
              errEl.hidden = false;
            }
            var input = form.querySelector('[name="' + field + '"]');
            if (input) input.classList.add('is-invalid');
          });
          showFormMessage(form, 'error', 'Te rugăm să corectezi câmpurile marcate.');
        } else if (err.error === 'rate_limited') {
          showFormMessage(form, 'error', err.message || 'Ai trimis deja o recenzie pentru acest produs astăzi.');
        } else {
          showFormMessage(form, 'error', 'Nu am putut trimite recenzia. Încearcă din nou mai târziu.');
        }
      })
      .catch(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Trimite recenzia';
        showFormMessage(form, 'error', 'Eroare de rețea. Verifică conexiunea și încearcă din nou.');
      });
    });
  }

  function bindForms() {
    // Form init per block (1 on each variant; one is hidden by viewport).
    document.querySelectorAll('.ts-reviews-form').forEach(function (form) {
      bindRatingPicker(form);
      bindSubmitForm(form);
      var cancel = form.querySelector('.ts-reviews-form__cancel');
      if (cancel) {
        cancel.addEventListener('click', function () {
          var wrap = form.closest('.ts-reviews-form-wrapper');
          if (wrap) wrap.hidden = true;
        });
      }
    });

    // "Scrie o recenzie" trigger — opens the form + scrolls to it.
    document.querySelectorAll('.ts-reviews-block__write').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var block = btn.closest('.ts-reviews-block');
        if (!block) return;
        var wrap = block.querySelector('.ts-reviews-form-wrapper');
        if (!wrap) return;
        wrap.hidden = false;
        // Small delay so the layout settles before scrolling.
        // NOTE: do NOT autofocus the first star — focus triggers the
        // hover-paint (the picker uses :focus = preview) which would
        // make 1★ look pre-selected. Let the user click.
        setTimeout(function () {
          wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      });
    });
  }

  function init() {
    var containers = document.querySelectorAll('.ts-reviews-block__list[data-handle]');
    containers.forEach(function (container) {
      loadInto(container, 1);
    });

    // Pagination click delegation — works for both variants
    document.querySelectorAll('.ts-reviews-block__pagination').forEach(function (pag) {
      pag.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-page]');
        if (!btn || btn.disabled) return;
        var page = parseInt(btn.getAttribute('data-page'), 10);
        if (!page || page < 1) return;
        var block = pag.closest('.ts-reviews-block');
        var listEl = block && block.querySelector('.ts-reviews-block__list');
        if (listEl) {
          loadInto(listEl, page);
          // Scroll into view for clarity
          block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    bindForms();
    bindTitleRatingScroll();
  }

  // Title-rating click → scroll to reviews. The section has
  // id="ts-reviews-anchor"; on mobile we also expand the parent
  // accordion <details> so the user lands on the visible block.
  function bindTitleRatingScroll() {
    document.querySelectorAll('[data-ts-scroll-to-reviews]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        // On mobile, the desktop section is display:none — scroll to
        // the visible accordion instead. Pick whichever one is visible.
        e.preventDefault();
        var section = document.querySelector('#ts-reviews-anchor');
        var accordion = document.querySelector('details:has(.ts-reviews--accordion)');
        var target = null;
        // Choose whichever is visible on this viewport.
        if (section && section.offsetParent !== null) target = section;
        else if (accordion && accordion.offsetParent !== null) target = accordion;
        if (!target) return;
        // Open the details on mobile if it's collapsed.
        if (target.tagName === 'DETAILS' && !target.open) target.open = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
